// Global Instructions Rule Applied!
//
// Resolves the dropdown options for a list screen's filter specs. `globalConfigs`-sourced
// options are read synchronously from the already-loaded cfg slice (zero fetch, instant).
// `fetch`-sourced options call the module's existing filter-options endpoint once on mount,
// deduped by fetcher reference (companies' Country + City come from a single companies call).
// Best-effort: a failed fetch leaves that filter's options empty rather than throwing —
// the control still shows any currently-selected chips, matching desktop behaviour.

import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAccessToken } from '../../../auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { FilterSpec, FilterOption } from './listDescriptors.ts'

export function useFilterOptions(filters?: FilterSpec[]): Record<string, FilterOption[]> {
  const specs = useMemo(() => filters ?? [], [filters])
  const globalConfigs = useSelector((s: any) => s?.globalConfigs)

  // Zero-fetch options resolved straight from the cfg slice.
  const globalOptions = useMemo(() => {
    const out: Record<string, FilterOption[]> = {}
    for (const spec of specs) {
      if (spec.options.kind === 'globalConfigs') {
        try {
          out[spec.key] = spec.options.select(globalConfigs) ?? []
        } catch {
          out[spec.key] = []
        }
      }
    }
    return out
  }, [globalConfigs, specs])

  // One-time fetched options (cities, contact-company). Best-effort.
  const [fetched, setFetched] = useState<Record<string, FilterOption[]>>({})
  const fetchKey = specs.map((s) => s.key).join('|')

  useEffect(() => {
    // Group fetch-based specs by their (shared) fetcher so we call each endpoint once.
    const groups = new Map<(t: string | null) => Promise<any>, Array<{ key: string; optionsKey: string }>>()
    for (const spec of specs) {
      if (spec.options.kind === 'fetch') {
        const cols = groups.get(spec.options.fetch) ?? []
        cols.push({ key: spec.key, optionsKey: spec.options.optionsKey })
        groups.set(spec.options.fetch, cols)
      }
    }
    if (groups.size === 0) return

    let cancelled = false
    const token = getAccessToken(store.getState().auth)

    groups.forEach((cols, fetchFn) => {
      fetchFn(token)
        .then((map: Record<string, Array<{ value: string | number; label: string }>>) => {
          if (cancelled) return
          setFetched((prev) => {
            const next = { ...prev }
            for (const c of cols) {
              next[c.key] = (map?.[c.optionsKey] ?? []).map((o) => ({ value: String(o.value), label: o.label }))
            }
            return next
          })
        })
        .catch(() => {
          /* best-effort — leave options empty on failure */
        })
    })

    return () => {
      cancelled = true
    }
    // fetchKey captures the module's filter set; specs is module-level stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey])

  return useMemo(() => ({ ...globalOptions, ...fetched }), [globalOptions, fetched])
}
